import React, { useState, useMemo } from "react";
import ReactJson from "react-json-view";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdSearch, MdContentCopy, MdOutlineClose } from "react-icons/md";
import AppButton from "./AppButton";

export default function ModernJsonReader({ data }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const vscodeTheme = {
    base00: "transparent", // Background hum container se control karenge
    base01: "#2D2D2D",
    base02: "#3C3C3C",
    base03: "#6A9955", // Comments/Info style
    base04: "#A5A5A5",
    base05: "#D4D4D4", // Default text
    base06: "#CCCCCC",
    base07: "#CCCCCC",
    base08: "#F44747",
    base09: "#CE9178", // Strings (Brownish Orange)
    base0A: "#DCDCAA",
    base0B: "#B5CEA8", // Numbers (Green)
    base0C: "#646695",
    base0D: "#9CDCFE", // Keys (Light Blue - Exact VS Code)
    base0E: "#C586C0",
    base0F: "#D16969",
  };

  const copyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success(t("JSON Copied Successfully!"));
  };

  // Recursive filter function to find matches in keys or values
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const s = searchTerm.toLowerCase();

    const filter = (obj) => {
      // Handle primitives
      if (typeof obj !== "object" || obj === null) {
        return String(obj).toLowerCase().includes(s) ? obj : undefined;
      }

      // Handle Arrays
      if (Array.isArray(obj)) {
        const filtered = obj.map(filter).filter((val) => val !== undefined);
        return filtered.length > 0 ? filtered : undefined;
      }

      // Handle Objects
      const filtered = {};
      let hasMatch = false;

      for (const key in obj) {
        // Match in key
        if (key.toLowerCase().includes(s)) {
          filtered[key] = obj[key];
          hasMatch = true;
        } else {
          // Deep match in value
          const result = filter(obj[key]);
          if (result !== undefined) {
            filtered[key] = result;
            hasMatch = true;
          }
        }
      }

      return hasMatch ? filtered : undefined;
    };

    return filter(data) || {};
  }, [data, searchTerm]);

  // Calculate actual line count from filtered JSON
  const lineCount = useMemo(() => {
    return JSON.stringify(filteredData, null, 2).split("\n").length;
  }, [filteredData]);

  return (
    <>
      <style>{`
        /* Remove background from collapse/expand icons */
        .vscode-json-container .icon-container {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          margin-right: 8px !important;
        }
        
        /* Style the triangle icons to look like VS Code */
        .vscode-json-container .icon-container .expanded-icon {
          color: #CCCCCC !important;
          background: transparent !important;
        }
        
        .vscode-json-container .icon-container .collapsed-icon {
          color: #CCCCCC !important;
          background: transparent !important;
        }
        
        /* Hover effect on icons like VS Code */
        .vscode-json-container .icon-container:hover {
          background: transparent !important;
          opacity: 0.7;
        }
        
        /* Remove any circular background from icons */
        .vscode-json-container svg {
          background: transparent !important;
        }
        
        /* Bracket colors matching VS Code */
        .vscode-json-container .brace-row .brace {
          color: #FFD700 !important;
        }
        
        /* Custom scrollbar to match VS Code */
        .custom-scrollbar::-webkit-scrollbar {
          width: 14px;
          height: 14px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1E1E1E;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #424242;
          border-radius: 0px;
          border: 3px solid #1E1E1E;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4E4E4E;
        }
        
        /* Object/Array size count styling */
        .vscode-json-container .node-ellipsis {
          color: #858585 !important;
          font-style: italic;
        }
      `}</style>

      <div className="bg-[#1E1E1E] rounded-lg shadow-2xl border border-[#333333] overflow-hidden flex flex-col h-[80vh] vscode-json-container">
        <div className="flex items-center justify-between p-2 bg-[#252526] border-b border-[#1E1E1E] gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="flex items-center px-3 py-1 bg-[#1E1E1E] text-[#969696] text-[11px] border-t border-t-[#0070C2] h-[32px]">
              <span className="italic select-none">{t("data.json")}</span>
            </div>

            <div className="relative w-full max-w-sm group">
              <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#858585] text-lg group-focus-within:text-[#0070C2]" />
              <input
                type="text"
                placeholder={t("Find in JSON...")}
                className="w-full bg-[#3C3C3C] text-[#CCCCCC] pl-9 pr-8 py-1 text-[12px] border focus:!border-[#0070C2] outline-none transition-all placeholder-[#858585] rounded-[4px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <MdOutlineClose
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#858585] cursor-pointer hover:text-white"
                  onClick={() => setSearchTerm("")}
                />
              )}
            </div>
          </div>

          <AppButton
            label={t("Copy")}
            onClick={copyJSON}
            variant="outlined"
            startIcon={<MdContentCopy size={14} />}
            className="!bg-[#333333] !text-[#CCCCCC] !border-none !h-[26px] !text-[11px] hover:!bg-[#444444]"
          />
        </div>

        <div className="flex-1 p-4 overflow-auto custom-scrollbar relative flex">
          <div className="hidden md:flex flex-col text-right pr-4 text-[#858585] text-[12px] select-none border-r border-[#333333] mr-4 leading-[22px]">
            {[...Array(lineCount)].map((_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>

          <div className="flex-1">
            <ReactJson
              src={filteredData}
              theme={vscodeTheme}
              iconStyle="triangle"
              indentWidth={4}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
              name={false}
              collapsed={searchTerm ? false : 1}
              style={{
                fontSize: "13px",
                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                backgroundColor: "transparent",
                lineHeight: "22px",
              }}
            />
          </div>
        </div>

        <div className="bg-[#0070C2] px-3 py-0.5 flex items-center justify-between text-white text-[10px] select-none">
          <div className="flex gap-4 uppercase font-medium">
            <span>{t("UTF-8")}</span>
            <span>{t("JSON")}</span>
          </div>
          <span>
            {searchTerm ? `${t("Search Results Filtered")}` : t("Ready")}
          </span>
        </div>
      </div>
    </>
  );
}
